/**
 * Two exhaustive maps for converting layer properties into the frontend `Layer["properties"]` shape:
 *
 * - `layerPropertiesConverters`: from the backend API union (`LayerInputProperties`)
 * - `layerPropertiesFromFileConverters`: from a `CompleteFile` descriptor
 *
 * Both are keyed on the full discriminant union of `Layer["properties"]["type"]` / `LayerInputProperties["type"]`,
 * so TypeScript will error on the Record if a new variant is added to either union without updating these maps.
 */

import {
  FileDescriptorRead,
  LayerInputProperties,
  PhysicalQuantity,
  instanceOfPhysicalQuantity,
} from "@nens/rana/api-v1";
import { combineObjects } from "@nens/shared-utils";

import { isTypedDescriptor } from "@/utils/is-descriptor-type";

import { CompleteFile } from "../types/file-reference-with-file";
import { Layer } from "../types/publication";
import { getDataTypeFromFile } from "../utils/get-data-type-from-file";
import { FILE_DATA_TYPE_TO_LAYER_TYPE } from "../utils/is-layer-typed";

// ─── API → frontend ──────────────────────────────────────────────────────────

// Allow undefined for all properties except the discriminant `type` property,
// so that we can combine with the file descriptor properties.
type Undefinedable<T> = T extends { type: string }
  ? {
      type: T["type"];
    } & {
      [K in Exclude<keyof T, "type">]-?: T[K] | undefined;
    }
  : never;

type FrontendLayerProperties = NonNullable<Layer["properties"]>;
type OptionalFrontendLayerProperties = Undefinedable<FrontendLayerProperties>;

const getPartialFrontendProperties = (
  properties: LayerInputProperties
): OptionalFrontendLayerProperties => {
  switch (properties.type) {
    case "generic":
      return {
        type: "generic",
        styleId: properties.styleId ?? undefined,
      };
    case "raster":
      return {
        type: "raster",
        styleId: properties.styleId ?? undefined,
        physicalQuantity: instanceOfPhysicalQuantity(properties.physicalQuantity)
          ? (properties.physicalQuantity as PhysicalQuantity)
          : undefined,
        minMax: properties.minMax as [number, number] | undefined,
        initialMinMax: properties.minMax as [number, number] | undefined,
      };
  }
};

// Uses the properties from the file descriptor as the base.
// Any properties from the API layer will override those from the file descriptor.
export function getPropertiesFromApiLayer(
  apiProperties: LayerInputProperties | null | undefined,
  completeFile: CompleteFile
): FrontendLayerProperties | null {
  const fileProperties = getPropertiesFromCompleteFile(completeFile);
  if (!apiProperties) return fileProperties;

  if (!fileProperties) {
    return null;
  }

  const frontendProperties = getPartialFrontendProperties(apiProperties);

  return combineObjects<FrontendLayerProperties>(fileProperties, frontendProperties);
}

// ─── CompleteFile → frontend ─────────────────────────────────────────────────

/**
 * `styleId` is not present on all meta union members, so use duck-typing.
 */
function getStyleIdFromDescriptor(descriptor: FileDescriptorRead | null): string | null {
  const meta = descriptor?.meta;
  return meta && "styleId" in meta ? (meta.styleId as string | null) ?? null : null;
}

export function getPropertiesFromCompleteFile(completeFile: CompleteFile): Layer["properties"] {
  const { file } = completeFile;
  const layerType = FILE_DATA_TYPE_TO_LAYER_TYPE[getDataTypeFromFile(file)];

  if (!layerType) return null;

  const descriptor = file.descriptor;

  switch (layerType) {
    case "raster": {
      if (!isTypedDescriptor(descriptor, "raster")) {
        return null;
      }
      const rangeType = descriptor.meta.rangeType;
      const minMax: [number, number] = [+rangeType.min.toFixed(1), +rangeType.max.toFixed(1)];
      return {
        type: "raster",
        styleId: descriptor.meta.styleId,
        physicalQuantity: rangeType?.physicalQuantity ?? "unknown",
        minMax,
        initialMinMax: minMax,
      };
    }
    case "generic":
      return {
        type: "generic",
        styleId: getStyleIdFromDescriptor(descriptor),
      };
  }
}
